// Location: src/components/admin/analytics/GameLifecycleControl.tsx
// Purpose: SuperAdmin controls for managing game lifecycle, seasons, and reward distribution
// Scope: Control Social Profile Game and Buzz Agent Game status, seasons, and settings

import React from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { Settings, ArrowLeft, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { SuperAdminGameControls } from '@/components/gamification/SuperAdminGameControls';
import { BuzzAgentGameControls } from '@/components/admin/BuzzAgentGameControls';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type { Id } from '@/convex/_generated/dataModel';

interface GameLifecycleControlProps {
	className?: string;
}

export function GameLifecycleControl({ className }: GameLifecycleControlProps) {
	const { user, isSuperAdmin, isAdmin } = useAuth();
	const navigate = useNavigate();

	// Allow admins to view, but only superadmins can modify
	if (!user || !isAdmin) {
		return (
			<div className='container mx-auto p-6'>
				<Card className='mt-6'>
					<CardContent>
						<div className='flex flex-col items-center justify-center space-y-6 p-8'>
							<Shield className='h-16 w-16 text-red-500' />
							<h2 className='text-2xl font-bold text-red-500'>
								Admin Access Required
							</h2>
							<p className='text-lg text-gray-400 text-center'>
								Only Admins can access Game Settings.
							</p>
						</div>
					</CardContent>
				</Card>
			</div>
		);
	}

	return (
		<div className={cn('space-y-6 my-20', className)}>
			{/* Header with Back Button */}
			<div>
				<Button
					variant='ghost'
					size='sm'
					onClick={() => navigate('/admin/analytics')}
					className='hover:bg-yellow-500/10'
				>
					<ArrowLeft className='h-4 w-4 mr-2' />
					Back to Analytics
				</Button>
			</div>
			<div className='flex items-center justify-between'>
				<div className='flex items-center gap-4'>
					<div>
						<h1 className='text-3xl font-bold text-white flex items-center gap-3'>
							<Settings className='h-8 w-8 text-yellow-500' />
							Game Settings
						</h1>
						<p className='text-muted-foreground mt-2'>
							Game settings, status, and reward distributions.
						</p>
					</div>
				</div>
				<Badge
					variant='outline'
					className={cn(
						'text-white border-0 px-4 py-2',
						isSuperAdmin
							? 'bg-gradient-to-r from-yellow-500 to-orange-600'
							: 'bg-gradient-to-r from-gray-500 to-gray-600'
					)}
				>
					<Shield className='h-3 w-3 mr-1' />
					{isSuperAdmin ? 'FULL CONTROL' : 'VIEW ONLY'}
				</Badge>
			</div>

			{/* Game Controls Tabs */}
			<Tabs defaultValue='social-profile' className='space-y-6'>
				<TabsList className='grid w-full grid-cols-3 bg-gray-900/50'>
					<TabsTrigger
						value='social-profile'
						className='data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-indigo-600 data-[state=active]:text-white'
					>
						Social Profile Game
					</TabsTrigger>
					<TabsTrigger
						value='buzz-agent'
						className='data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-500 data-[state=active]:to-pink-600 data-[state=active]:text-white'
					>
						Buzz Agent Game
					</TabsTrigger>
					<TabsTrigger
						value='rewards'
						className='data-[state=active]:bg-gradient-to-r data-[state=active]:from-emerald-500 data-[state=active]:to-green-600 data-[state=active]:text-white'
					>
						Reward Distribution
					</TabsTrigger>
				</TabsList>

				{/* Social Profile Game Controls */}
				<TabsContent value='social-profile' className='space-y-6'>
					<Card className='bg-gradient-to-br from-blue-500/5 to-indigo-500/5 border-blue-500/20'>
						<CardHeader>
							<CardTitle className='text-xl'>
								🔥 Social Profile Game Settings
							</CardTitle>
						</CardHeader>
						<CardContent>
							<SuperAdminGameControls
								userId={user._id as unknown as Id<'users'>}
								readOnly={!isSuperAdmin}
							/>
						</CardContent>
					</Card>
				</TabsContent>

				{/* Buzz Agent Game Controls */}
				<TabsContent value='buzz-agent' className='space-y-6'>
					<BuzzAgentGameControls />
				</TabsContent>

				{/* Reward Distribution */}
				<TabsContent value='rewards' className='space-y-6'>
					<Card className='bg-gradient-to-br from-emerald-500/5 to-green-500/5 border-emerald-500/20'>
						<CardHeader>
							<CardTitle className='text-xl'>
								💰 Reward Distribution Settings
							</CardTitle>
						</CardHeader>
						<CardContent>
							<div className='space-y-4'>
								<div className='text-center py-12'>
									<Settings className='h-12 w-12 mx-auto mb-4 text-emerald-500/50' />
									<h3 className='text-lg font-semibold mb-2'>
										💰 Reward Distribution Controls
									</h3>
									<p className='text-sm text-muted-foreground'>
										Configure KARMA token rewards,
										distribution schedules, and payout
										thresholds. Set up automated reward
										calculations based on game performance.
									</p>
								</div>
							</div>
						</CardContent>
					</Card>
				</TabsContent>
			</Tabs>

			{/* Info Card */}
			{/* <Card className='bg-gradient-to-br from-yellow-500/5 to-orange-500/5 border-yellow-500/20'>
				<CardContent className='p-6'>
					<div className='flex items-start gap-4'>
						<div className='p-3 bg-yellow-500/10 rounded-xl'>
							<Shield className='h-6 w-6 text-yellow-500' />
						</div>
						<div className='flex-1'>
							<h3 className='font-semibold mb-2'>
								SuperAdmin Game Lifecycle Control
							</h3>
							<p className='text-sm text-muted-foreground mb-3'>
								As a SuperAdmin, you have complete control over
								all game lifecycles. You can start/stop games,
								create new seasons, adjust scoring parameters,
								and distribute rewards. All changes are logged
								and tracked for audit purposes.
							</p>
							<div className='flex gap-4 text-sm'>
								<div>
									<span className='text-muted-foreground'>
										Active Games:
									</span>
									<span className='ml-2 font-medium'>
										2/4
									</span>
								</div>
								<div>
									<span className='text-muted-foreground'>
										Current Season:
									</span>
									<span className='ml-2 font-medium text-yellow-500'>
										Season 1
									</span>
								</div>
								<div>
									<span className='text-muted-foreground'>
										Total Rewards Pool:
									</span>
									<span className='ml-2 font-medium text-emerald-600'>
										100,000 KARMA
									</span>
								</div>
							</div>
						</div>
					</div>
				</CardContent>
			</Card> */}
		</div>
	);
}
